import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { useMediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { Typography } from '@euler/app/components/typography';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ConstructionProcedureType, MediaObject } from '@euler/model';
import {
  ConstructionJob,
  ConstructionJobMedia,
  taskMediaToMediaObject,
} from '@euler/model/task-detail';
import { delayed } from '@euler/utils';
import { usePrevious } from '@euler/utils/hooks';
import { MotiView } from 'moti';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

type JobMedia = ConstructionJobMedia & {
  procedureName: string;
  procedureType: ConstructionProcedureType;
};

export const JobPageView = memo(
  ({
    width,
    active,
    job,
    style,
  }: {
    width: number;
    active?: boolean;
    style?: StyleProp<ViewStyle>;
    job: ConstructionJob;
  }) => {
    const { rpx } = useResponsiveContext();
    const [paddingLeft, paddingRight, { bottom }] = useScreenContentPadding();
    const [visible, setVisible] = useState(active);
    const previousActive = usePrevious(active);

    const medias = useMemo(() => {
      return job.procedures.flatMap<JobMedia>(p =>
        p.medias.map(x => ({
          ...x,
          procedureName: p.name,
          procedureType: p.type,
        })),
      );
    }, [job]);

    const mediaObjects = useMemo(
      () =>
        job.procedures.flatMap(p =>
          p.medias.map(x => taskMediaToMediaObject(x)),
        ),
      [job],
    );

    const {
      isImageViewerOpen,
      setIsImageViewerOpen,
      imageUrls,
      currentImageIndex,
      onMediaPress,
    } = useMediaCarousel(mediaObjects);

    const itemHeight = rpx(668) - bottom;
    const itemWidth = itemHeight * (528 / 668);
    const mediaHeight = itemWidth;

    useEffect(() => {
      if (!active) {
        if (previousActive) {
          return delayed(() => {
            setVisible(false);
          }, 350);
        }
      } else {
        setVisible(true);
      }
    }, [active, previousActive, visible]);

    const renderItem: ListRenderItem<JobMedia> = useCallback(
      ({ item, index }) => {
        return (
          <Cell
            media={item}
            index={index}
            onPress={onMediaPress}
            mediaStyle={{
              width: itemWidth,
              height: mediaHeight,
            }}
            css={`
              width: ${itemWidth}px;
              height: ${itemHeight}px;
              margin-right: ${rpx(40)}px;
            `}
          />
        );
      },
      [itemHeight, itemWidth, mediaHeight, onMediaPress, rpx],
    );

    return (
      <View
        css={`
          flex-grow: 0;
          flex-shrink: 0;
          width: ${width}px;
        `}
        style={style}
      >
        {visible ? (
          medias.length > 0 ? (
            <>
              <ImageViewer
                images={imageUrls}
                index={currentImageIndex}
                show={isImageViewerOpen}
                onDismiss={() => setIsImageViewerOpen(false)}
              />
              <FlatList
                data={medias}
                renderItem={renderItem}
                keyExtractor={x => x.cid}
                horizontal
                showsHorizontalScrollIndicator={Platform.OS === 'web'}
                contentContainerStyle={{
                  paddingLeft,
                  paddingRight,
                  alignItems: 'center',
                }}
              />
            </>
          ) : (
            <EmptyPlaceholder
              text="暂无相关施工图片/视频"
              iconSize={rpx(160)}
              textStyle={{
                fontSize: rpx(24),
                marginTop: rpx(16),
              }}
              css={`
                flex: 1;
              `}
            />
          )
        ) : null}
      </View>
    );
  },
);

const Cell = memo(
  ({
    media,
    index,
    style,
    mediaStyle,
    onPress,
  }: {
    media: JobMedia;
    index: number;
    style?: StyleProp<ViewStyle>;
    mediaStyle?: StyleProp<ViewStyle>;
    onPress?: (mediaObject: MediaObject, index: number) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const mediaObj = useMemo(() => taskMediaToMediaObject(media), [media]);

    const handlePress = useCallback(() => {
      onPress?.(mediaObj, index);
    }, [onPress, mediaObj, index]);

    return (
      <MotiView
        from={{ opacity: 0, translateX: 150 }}
        animate={{ opacity: 1, translateX: 0 }}
        transition={{ type: 'timing', duration: 300 }}
        delay={Math.min(1500, index * 50) + 150}
        css={`
          border-radius: ${rpx(20)}px;
          overflow: hidden;
          background-color: ${theme.panel.bg};
        `}
        style={style}
      >
        <TouchableOpacity
          onPress={handlePress}
          css={`
            flex: 1;
          `}
        >
          <View
            style={mediaStyle}
            css={`
              border-radius: ${rpx(20)}px;
              overflow: hidden;
            `}
          >
            <MediaCoverView
              media={mediaObj}
              TouchableComponent={TouchableOpacity}
              onPress={handlePress}
              activityIndicatorSize={rpx(80)}
              noPlaceholder={media.type === 'image' && media.url.includes('#')}
              containerStyle={[mediaStyle]}
              placeholderStyle={{
                backgroundColor: 'transparent',
              }}
              css={`
                flex-shrink: 0;
              `}
            />
          </View>
          <Center
            css={`
              flex: 1;
            `}
          >
            <Typography.Label
              medium
              color={theme.task.construction.jobDetailPage.media.titleTextColor}
              size={rpx(32)}
              lineHeight={rpx(48)}
              numberOfLines={1}
            >
              {media.procedureName}
            </Typography.Label>
          </Center>
        </TouchableOpacity>
      </MotiView>
    );
  },
);
